/**
 * @file BackdropImageRow.tsx
 * @description Рисунок фона сайта или страницы (phase19.16 Р1; uiux-design-r1
 * §1; партия SB120, этап 2 — uiux-sb120-stage2 §2 «Фон и рамка», §10
 * вопрос 4; данные db-design-sb120-stage2 §1 `backdrop.images[]`):
 *
 * ```
 * ┌[мини] Рисунок 1 ─────────────────── [Убрать]┐
 * │ Место  схема окна ●  x [ 100 ] %  y [ 50 ] % │  DecorPositionField, 9 мест
 * │ Размер на ПК  ├───●──┤ [ 300 ] px            │
 * │  на планшете 206 · на телефоне 131 px  Своё › │  DeviceValueRow
 * │ Яркость       ├──●───┤ [ 35 ] %  ↺ как было  │
 * │ [ ] Скрыть на телефоне                       │
 * └──────────────────────────────────────────────┘
 * ```
 *
 * Вместо прежних углов [↖][↗][↙][↘] и S/M/L — точка x/y по окну (без 96 px
 * сверху и 48 снизу) и ширина px; «ПК» у рисунков — от 1024. Планшет и
 * телефон — своё число или доля ПК (нет ключа). Яркость: нет ключа — как
 * было (30 % на телефоне, 35 % с 768). `onChange` получает рисунок целиком
 * (`null` — убрать): необязательные ключи снимаются, а не пишутся пустыми.
 */

import { BACKDROP_SIZE_V1, SITE_BACKDROP_AREA, SITE_BACKDROP_SIZE_SHARE, SITE_THEME_LIMITS, type SiteBackdropImage } from "@chaiberi/shared";
import { Trash2 } from "lucide-react";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { CheckRow } from "../CheckRow";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { DeviceValueRow } from "../../site-builder/section/fields/DeviceValueRow";
import { DecorPositionField } from "../../site-builder/section/look/DecorPositionField";
import { contentMediaUrl } from "../../../lib/api-content-media";
import { BACKDROP_SIZE_PRESETS } from "../../../lib/site-view/appearance-numbers";
import { backdropDeviceSummary, backdropTooWideOnPhone, BACKDROP_DESKTOP_FROM, withOptional, type BackdropOptionalKey } from "../../../lib/site-view/backdrop-images";

const { backdropSize: SIZE, backdropOpacity: OP } = SITE_THEME_LIMITS;
/** Размер по умолчанию — прежний M. */
const SIZE_M = BACKDROP_SIZE_V1.m.size;
const AREA_HINT = `Схема — окно сайта без ${SITE_BACKDROP_AREA.top} px сверху (шапка) и ${SITE_BACKDROP_AREA.bottom} px снизу. Рисунок стоит по окну, а не по рамке.`;

interface Props {
  image: SiteBackdropImage;
  /** Номер для подписи «Рисунок 1». */
  index: number;
  /** Рисунок целиком; `null` — убрать. */
  onChange: (image: SiteBackdropImage | null) => void;
}

export function BackdropImageRow({ image, index, onChange }: Props) {
  const name = `Рисунок ${index + 1}`;
  const optional = (key: BackdropOptionalKey, value: number | undefined) => onChange(withOptional(image, key, value));
  const share = (device: "tablet" | "phone") => Math.round(image.size * SITE_BACKDROP_SIZE_SHARE[device]);
  const deviceSize = (key: "sizeTablet" | "sizePhone", device: "tablet" | "phone", label: string) => (
    <NumberSliderField
      label={`${label}, ${name.toLowerCase()}`}
      value={image[key]}
      inherit={{ value: share(device), label: "по доле" }}
      min={SIZE.min}
      max={SIZE.max}
      step={1}
      unit="px"
      onChange={(v) => optional(key, v)}
    />
  );

  return (
    <div role="group" aria-label={name} className="flex min-w-0 flex-col gap-3 rounded-xl bg-surface-lowest p-3 [box-shadow:inset_0_0_0_1px_var(--border)]">
      <div className="flex min-w-0 items-center gap-3">
        <img src={contentMediaUrl(image.mediaId)} alt="" className="size-14 shrink-0 rounded-md bg-surface-low object-contain" />
        <span className="type-body min-w-0 flex-1 font-medium text-on-surface">{name}</span>
        <Button variant="ghost" className="shrink-0" onClick={() => onChange(null)} aria-label={`Убрать: ${name.toLowerCase()}`}>
          <Trash2 aria-hidden />
          Убрать
        </Button>
      </div>
      <DecorPositionField
        label="Место"
        value={{ x: image.x, y: image.y }}
        onChange={(point) => onChange({ ...image, ...point })}
        schemeClassName="aspect-video"
        hint={AREA_HINT}
      />
      <NumberSliderField
        label="Размер на ПК"
        value={image.size}
        inherit={{ value: SIZE_M, label: `M ${SIZE_M}` }}
        min={SIZE.min}
        max={SIZE.max}
        step={1}
        largeStep={20}
        unit="px"
        presets={BACKDROP_SIZE_PRESETS}
        hint={`Ширина рисунка на экране от ${BACKDROP_DESKTOP_FROM} px.`}
        onChange={(v) => onChange({ ...image, size: v ?? SIZE_M })}
      />
      <DeviceValueRow summary={backdropDeviceSummary(image)} own={image.sizeTablet !== undefined || image.sizePhone !== undefined} label={`Размер на планшете и телефоне, ${name.toLowerCase()}`}>
        <div className="flex min-w-0 flex-col gap-3">
          {deviceSize("sizeTablet", "tablet", "На планшете")}
          {deviceSize("sizePhone", "phone", "На телефоне")}
        </div>
      </DeviceValueRow>
      {backdropTooWideOnPhone(image) ? (
        <NoticeStrip tone="warning">Рисунок шире 40 % экрана телефона — закроет текст. Уменьшите его на телефоне или скройте там.</NoticeStrip>
      ) : null}
      <NumberSliderField
        label="Яркость"
        value={image.opacity}
        inherit={{ value: OP.wide, label: "как было" }}
        min={OP.min}
        max={OP.max}
        step={1}
        unit="%"
        hint={`Как было: ${OP.phone} % на телефоне, ${OP.wide} % с 768 px.`}
        onChange={(v) => optional("opacity", v)}
      />
      <CheckRow checked={image.hideOnMobile} onChange={(hideOnMobile) => onChange({ ...image, hideOnMobile })}>
        Скрыть на телефоне
      </CheckRow>
    </div>
  );
}
